<template>
  <div class="article-rating-container">
    <!-- Rating Display and Input -->
    <div class="rating-section">
      <div class="rating-header">
        <h4 class="rating-title">
          <i class="fas fa-star"></i>
          Rate this article
        </h4>
        <div v-if="articleStats" class="rating-stats">
          <span class="average-rating">{{ articleStats.averageRating }}/5</span>
          <span class="vote-count">({{ articleStats.totalVotes }} {{ articleStats.totalVotes === 1 ? 'vote' : 'votes' }})</span>
        </div>
      </div>

      <!-- Authentication Required Message -->
      <div v-if="!isAuthenticated" class="auth-required">
        <p class="auth-message">
          <i class="fas fa-user-lock"></i>
          Please <button @click="$emit('show-auth')" class="auth-link">sign in</button> to rate this article
        </p>
      </div>

      <!-- Rating Input (for authenticated users) -->
      <div v-else class="rating-input">
        <!-- Star Rating -->
        <div class="star-rating">
          <button
            v-for="star in 5"
            :key="star"
            @click="setRating(star)"
            @mouseover="hoverRating = star"
            @mouseleave="hoverRating = 0"
            :class="['star-btn', {
              'filled': star <= (hoverRating || currentUserRating || 0),
              'hover': hoverRating > 0
            }]"
            :disabled="isSubmitting"
            :title="`Rate ${star} star${star > 1 ? 's' : ''}`"
          >
            <i class="fas fa-star"></i>
          </button>
        </div>

        <!-- Rating Labels -->
        <div class="rating-labels">
          <span v-if="hoverRating" class="rating-label">
            {{ getRatingLabel(hoverRating) }}
          </span>
          <span v-else-if="currentUserRating" class="current-rating">
            Your rating: {{ currentUserRating }}/5 ({{ getRatingLabel(currentUserRating) }})
          </span>
          <span v-else class="no-rating">
            Click a star to rate
          </span>
        </div>

        <!-- Submit/Update Button -->
        <button
          v-if="selectedRating !== null && selectedRating !== currentUserRating"
          @click="submitVote"
          :disabled="isSubmitting"
          class="submit-rating-btn"
        >
          <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-check"></i>
          {{ hasVoted ? 'Update Rating' : 'Submit Rating' }}
        </button>
      </div>
    </div>

    <!-- Rating Breakdown (if there are votes) -->
    <div v-if="articleStats && articleStats.totalVotes > 0" class="rating-breakdown">
      <h5 class="breakdown-title">Rating Breakdown</h5>
      <div class="breakdown-bars">
        <div
          v-for="rating in [5, 4, 3, 2, 1]"
          :key="rating"
          class="breakdown-row"
        >
          <span class="rating-number">{{ rating }}</span>
          <i class="fas fa-star rating-star"></i>
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: getPercentage(rating) + '%' }"
            ></div>
          </div>
          <span class="rating-count">{{ getRatingCount(rating) }}</span>
        </div>
      </div>
    </div>

    <!-- Success/Error Messages -->
    <div v-if="message" :class="['message', messageType]">
      <i :class="['fas', messageType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle']"></i>
      {{ message }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArticleRating',
  props: {
    articleId: {
      type: String,
      required: false
    },
    articleSlug: {
      type: String,
      required: false
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    authToken: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      articleStats: null,
      currentUserRating: null,
      hasVoted: false,
      hoverRating: 0,
      selectedRating: null,
      isSubmitting: false,
      isLoading: false,
      message: '',
      messageType: 'success'
    };
  },
  computed: {
    apiUrl() {
      return process.env.VUE_APP_API_URL;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      if (!this.articleId && !this.articleSlug) return;
      
      this.isLoading = true;
      try {
        // Load article stats
        await this.loadArticleStats();
        
        // Load user's vote if authenticated
        if (this.isAuthenticated) {
          await this.loadUserVote();
        }
      } catch (error) {
        console.error('Error loading rating data:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async loadArticleStats() {
      const endpoint = this.articleSlug 
        ? `${this.apiUrl}/api/articles/slug/${this.articleSlug}/stats`
        : `${this.apiUrl}/api/articles/${this.articleId}/stats`;

      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        this.articleStats = data.data;
      }
    },

    async loadUserVote() {
      if (!this.authToken) return;

      const endpoint = this.articleSlug 
        ? `${this.apiUrl}/api/articles/slug/${this.articleSlug}/vote`
        : `${this.apiUrl}/api/articles/${this.articleId}/vote`;

      try {
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${this.authToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.hasVoted = data.data.hasVoted;
          this.currentUserRating = data.data.vote?.rating || null;
          this.selectedRating = this.currentUserRating;
        }
      } catch (error) {
        console.error('Error loading user vote:', error);
      }
    },

    setRating(rating) {
      if (this.isSubmitting) return;
      this.selectedRating = rating;
    },

    async submitVote() {
      if (!this.isAuthenticated || !this.authToken || this.selectedRating === null) return;

      this.isSubmitting = true;
      this.message = '';

      try {
        const response = await fetch(`${this.apiUrl}/api/articles/vote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.authToken}`
          },
          body: JSON.stringify({
            articleId: this.articleId,
            articleSlug: this.articleSlug,
            rating: this.selectedRating
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          // Update local data
          this.currentUserRating = this.selectedRating;
          this.hasVoted = true;
          this.articleStats = data.data.articleStats;
          
          // Show success message
          this.showMessage(
            this.hasVoted ? 'Rating updated successfully!' : 'Rating submitted successfully!', 
            'success'
          );

          // Emit event for parent components
          this.$emit('vote-submitted', {
            rating: this.selectedRating,
            stats: this.articleStats
          });
        } else {
          const errorData = await response.json();
          this.showMessage(errorData.message || 'Failed to submit rating', 'error');
        }
      } catch (error) {
        console.error('Error submitting vote:', error);
        this.showMessage('Network error. Please try again.', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    getRatingLabel(rating) {
      const labels = {
        1: 'Poor',
        2: 'Fair', 
        3: 'Good',
        4: 'Very Good',
        5: 'Excellent'
      };
      return labels[rating] || '';
    },

    getRatingCount(rating) {
      if (!this.articleStats?.ratingDistribution) return 0;
      return this.articleStats.ratingDistribution[`rating${rating}`] || 0;
    },

    getPercentage(rating) {
      if (!this.articleStats?.totalVotes) return 0;
      const count = this.getRatingCount(rating);
      return Math.round((count / this.articleStats.totalVotes) * 100);
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
      setTimeout(() => {
        this.message = '';
      }, 3000);
    }
  },
  watch: {
    articleId() {
      this.loadData();
    },
    articleSlug() {
      this.loadData();
    },
    isAuthenticated(newVal) {
      if (newVal) {
        this.loadUserVote();
      } else {
        this.currentUserRating = null;
        this.hasVoted = false;
        this.selectedRating = null;
      }
    }
  }
};
</script>

<style scoped>
.article-rating-container {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin: 16px 0;
  transition: all 0.3s ease;
}

.rating-section {
  margin-bottom: 20px;
}

.rating-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.rating-title {
  color: #2d3748;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating-title i {
  color: #f59e0b;
}

.rating-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
}

.average-rating {
  font-weight: 600;
  color: #f59e0b;
  font-size: 16px;
}

.auth-required {
  padding: 16px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  text-align: center;
}

.auth-message {
  margin: 0;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.auth-link {
  color: #2563eb;
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.auth-link:hover {
  color: #1d4ed8;
}

.rating-input {
  text-align: center;
}

.star-rating {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 12px;
}

.star-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #d1d5db;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.star-btn:not(:disabled):hover {
  transform: scale(1.1);
}

.star-btn.filled {
  color: #f59e0b;
}

.star-btn.hover {
  color: #fbbf24;
}

.star-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.rating-labels {
  min-height: 24px;
  margin-bottom: 16px;
}

.rating-label,
.current-rating,
.no-rating {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.current-rating {
  color: #059669;
}

.submit-rating-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
}

.submit-rating-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.submit-rating-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.rating-breakdown {
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
}

.breakdown-title {
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.breakdown-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.rating-number {
  font-weight: 600;
  color: #374151;
  width: 12px;
}

.rating-star {
  color: #f59e0b;
  font-size: 12px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #f59e0b;
  transition: width 0.3s ease;
}

.rating-count {
  font-size: 12px;
  color: #64748b;
  width: 20px;
  text-align: right;
}

.message {
  padding: 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  margin-top: 12px;
}

.message.success {
  background: #d1fae5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.message.error {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fca5a5;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .article-rating-container {
    padding: 16px;
  }

  .rating-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .star-btn {
    font-size: 24px;
  }

  .breakdown-row {
    font-size: 14px;
  }
}
</style>